import { useEffect, useState, useRef } from 'react';
import { useChatComposerDraft } from "./useChatComposerDraft";
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@clerk/clerk-react';

import { createEmailThread } from '../reducers/chatReducer';
import { getEmailThreadService } from '../utils/chatService';
import { handleChatMessage, readChatSSEFrames, replaceChatMessageText } from '../utils/handleChatMessages';
import { readFileAsBase64 } from "../utils/fileUtils";
import useEventCallback from './useEventCallback';
import { clearAssistantUnavailable, setAssistantUnavailable } from '../reducers/assistantReducer';
import {
  CHAT_UNAVAILABLE_CODE,
  isChatUnavailableError,
  isChatUnavailablePayload,
  responseToChatUnavailableError,
} from '../utils/chatAvailability';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const useEmailChat = ({ selectedEmail, assistant }) => {
  const dispatch = useDispatch();
  const { getToken } = useAuth();
  const messagesEndRef = useRef(null);
  const getThreadsCalled = useRef(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [thinkingFeed, setThinkingFeed] = useState([]);
  const [threadIsLoaded, setThreadIsLoaded] = useState(false);
  const {
    resetChatBoxInput,
    setResetChatBoxInput,
    restoredChatBoxInput,
    setRestoredChatBoxInput,
    clearComposer,
    restoreComposerDraft,
  } = useChatComposerDraft();

  const emailChat = useSelector((state) =>
    state.chats?.find((chat) => chat.emailId === selectedEmail?.id && chat.mailIntegrationId === selectedEmail?.mailIntegrationId)
  );

  const initializeEmailThread = useEventCallback(() => getOrCreateEmailThread());

  useEffect(() => {
    if (selectedEmail?.id) {
      // Reset states when email changes
      setChatMessages([]);
      setThreadIsLoaded(false);
      setMessageLoading(true);
      getThreadsCalled.current = false;
  
      const initializeThread = async () => {
        try {
          await initializeEmailThread();
        } catch (error) {
          console.error("Error initializing email thread:", error);
        }
      };
      
      initializeThread();
    }
  }, [initializeEmailThread, selectedEmail?.id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages]);

  const getOrCreateEmailThread = async () => {
    if (getThreadsCalled.current) return;
    getThreadsCalled.current = true;
    setThreadIsLoaded(false);
    setMessageLoading(true);

    try {
      // URLSearchParams stringifies whatever it is given, so an absent
      // mailIntegrationId used to travel as the literal "undefined" and land in
      // a uuid comparison. Omit the key instead: the server already treats a
      // missing mailbox id as an unresolvable conversation.
      const params = new URLSearchParams({
        chatId: emailChat?.id || "",
        assistantId: assistant.id,
        emailId: selectedEmail.id,
        emailSubject: selectedEmail.subject,
        emailFrom: selectedEmail.from,
        emailSnippet: selectedEmail.snippet,
        emailActions: JSON.stringify(selectedEmail.actions),
      });
      if (typeof selectedEmail.mailIntegrationId === "string"
        && selectedEmail.mailIntegrationId.length > 0) {
        params.set("mailIntegrationId", selectedEmail.mailIntegrationId);
      }

      const response = await getEmailThreadService(getToken, params);

      if (!response.ok) {
        throw new Error("Failed to fetch email thread");
      }

      let openingSucceeded = false;
      await readChatSSEFrames(response, (data) => {

          // The stream opened with a 200 long before the failure happened, so
          // the server reports a dead chat surface as a frame, not a status.
          // Without this the frame is silently ignored and the pane spins.
          if (isChatUnavailablePayload(data)) {
            dispatch(setAssistantUnavailable({ reason: data.error.code }));
            return;
          }
          if (data.type === 'thinking') {
            setThinkingFeed((prev) => [...prev, { stage: data.stage, name: data.name, text: data.label || 'Working...' }]);
            return;
          }
          if (data.type === 'assistant_delta' || data.type === 'assistant_final') {
            setChatMessages((prev) => {
              const index = prev.findIndex((message) => message.__openingProvisional);
              const provisional = { role: 'assistant', __openingProvisional: true, content: [{ text: { value: '' } }] };
              const target = index === -1 ? provisional : prev[index];
              const text = data.type === 'assistant_final'
                ? (data.text || '')
                : `${target.content?.[0]?.text?.value || ''}${data.text || ''}`;
              const updatedTarget = replaceChatMessageText(target, text);

              return index === -1
                ? [...prev, updatedTarget]
                : prev.map((message, messageIndex) => (
                  messageIndex === index ? updatedTarget : message
                ));
            });
            return;
          }
          if (data.messages) {
            if (data.messages.length > 0) setChatMessages(() => [...data.messages]);
            openingSucceeded = true;
          }
          
          if (data.chatId) {
            dispatch(createEmailThread({
              chatId: data.chatId,
              emailId: selectedEmail.id,
              mailIntegrationId: selectedEmail.mailIntegrationId,
            }));
            openingSucceeded = true;
          }
      });
      // Opening streams do not emit chat_complete, but a normal EOF after an
      // authenticated transcript/chat-id frame is enough evidence to recover
      // the composer. A typed fault returned above never reaches this line.
      if (openingSucceeded) dispatch(clearAssistantUnavailable());
    } catch (error) {
      if (isChatUnavailableError(error)) {
        dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
      }
      console.error("Error creating email thread:", error);
    } finally {
      setMessageLoading(false);
      setThreadIsLoaded(true);
      getThreadsCalled.current = false;
    }
  };

  // TODO: This is a temporary function to restart the email thread
  const handleSendMessage = async (message, file) => {
    if (!message.trim()) return;

    setChatMessages((prevMessages) => [
      ...prevMessages,
      {
        role: "user",
        content: [{ text: { value: message } }],
        metadata: file
          ? { file_name: file?.name, file_type: file?.type }
          : {},
      },
    ]);
    setMessageLoading(true);
    // Clear on send: the turn is away, and a second Enter must not re-send it.
    clearComposer();
    setThinkingFeed([]);

    try {
      const token = await getToken();
      const base64File = file ? await readFileAsBase64(file) : null;

      const messageResponse = await fetch(
        `${API_BASE_URL}/api/assistant/send_message`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            assistantId: assistant.id,
            chatId: emailChat?.id,
            file: file 
              ? {
                  base64: base64File,
                  name: file?.name,
                  type: file?.type
                } 
              : null,
          }),
        }
      );

      if (!messageResponse.ok) {
        throw await responseToChatUnavailableError(messageResponse);
      }

      const newAssistantMessage = {
        role: "assistant",
        content: [{ text: { value: "" } }],
        chat_id: emailChat?.id,
      };
      setChatMessages((prevMessages) => [...prevMessages, newAssistantMessage]);
      // The server advances the rotating Responses handle inside this request;
      // the browser carries only its stable conversation id between turns.
      const terminalPayload = await handleChatMessage(messageResponse, setThinkingFeed, setChatMessages);
      if (isChatUnavailablePayload(terminalPayload)) {
        dispatch(setAssistantUnavailable({ reason: terminalPayload.error.code }));
        restoreComposerDraft(message, file);
      } else if (terminalPayload?.type === 'chat_complete') {
        dispatch(clearAssistantUnavailable());
      } else {
        // A stream that ended without chat_complete is a failed turn too --
        // leaving the box cleared would lose what the user typed.
        restoreComposerDraft(message, file);
      }
    } catch (error) {
      if (isChatUnavailableError(error)) {
        dispatch(setAssistantUnavailable({ reason: error?.response?.data?.error?.code || CHAT_UNAVAILABLE_CODE }));
      }
      console.error("Error getting AI response:", error);
      restoreComposerDraft(message, file);
      setChatMessages((prevMessages) => [
        ...prevMessages,
        {
          role: "assistant",
          content: [{ text: { value: "Sorry, there was an error processing your request" } }],
        },
      ]);
    } finally {
      setMessageLoading(false);
      setThinkingFeed([]);
    }
  };

  return {
    chatMessages,
    messageLoading,
    threadIsLoaded,
    resetChatBoxInput,
    setResetChatBoxInput,
    restoredChatBoxInput,
    setRestoredChatBoxInput,
    handleSendMessage,
    messagesEndRef,
    thinkingFeed,
    emailChat,
  };
};
